// TimeFrameSelector.jsx
import React, { useEffect, useMemo, useState } from "react";
import { FormGroup, TimeSelect } from "../Util/Form.elements";
import timeFrameData from "../Util/timeFrameData.json";
import { MenuItem, Box } from "@mui/material";
import { Hourglass, ChevronIcon } from "@/icons";

const TimeFrameSelector = ({ onSelectTimeFrame, value, disabled = false }) => {
    const [options, setOptions] = useState([]);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setOptions(timeFrameData);
    }, []);

    useEffect(() => {
        if (disabled) setOpen(false);
    }, [disabled]);

    const optionsMap = useMemo(() => {
        const m = new Map();
        (options || []).forEach((o) => m.set(o.value, o));
        return m;
    }, [options]);

    const handleTimeFrameChange = (e) => {
        if (disabled) return;

        const v = e.target.value;

        if (v === "all") {
            onSelectTimeFrame({ fromDate: null, toDate: null, value: v });
            return;
        }

        const daysToSubtract = parseInt(v, 10);
        const now = new Date();
        const from = new Date();
        from.setDate(now.getDate() - daysToSubtract);

        onSelectTimeFrame({
            fromDate: from.toISOString(),
            toDate: now.toISOString(),
            value: v,
        });
    };

    return (
        <FormGroup>
            <TimeSelect
                value={value}
                onChange={handleTimeFrameChange}
                disabled={disabled}
                open={disabled ? false : open}
                onOpen={() => !disabled && setOpen(true)}
                onClose={() => setOpen(false)}
                IconComponent={() => null}
                renderValue={(selected) => {
                    const label = optionsMap.get(selected)?.label || "All-Time";

                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", minWidth: 0, opacity: disabled ? 0.6 : 1 }}>
                            <Hourglass style={{ width: 18, height: 18, opacity: 0.9, flex: "0 0 auto" }} />

                            <Box
                                component="span"
                                sx={{
                                    flex: "1 1 auto",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {label}
                            </Box>

                            <ChevronIcon
                                style={{
                                    flex: "0 0 auto",
                                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 160ms ease",
                                }}
                            />
                        </Box>
                    );
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            mt: 1,
                            borderRadius: "12px",
                            overflow: "hidden",
                            background: "rgba(41, 56, 77, 0.85)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(128, 149, 183, 0.22)",
                        },
                    },
                    MenuListProps: {
                        sx: {
                            py: 0.5,
                            "& .MuiMenuItem-root": {
                                color: "#fff",
                                fontSize: "14px",
                                py: 1,
                                "&:hover": { backgroundColor: "rgba(58, 80, 110, 0.55)" },
                                "&.Mui-selected": { backgroundColor: "rgba(58, 80, 110, 0.7)" },
                                "&.Mui-selected:hover": { backgroundColor: "rgba(58, 80, 110, 0.75)" },
                            },
                        },
                    },
                }}
            >
                {options.map((option) => (
                    <MenuItem key={option.key} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </TimeSelect>
        </FormGroup>
    );
};

export default TimeFrameSelector;